import React, { useContext, useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import Table from 'components/Table'
import Button from 'components/Button'
import Modal from 'components/Modal'
import Select from 'components/Select'
import SearchInput from 'components/SearchInput'
import ItemsPerPageDropdown from 'components/ItemsPerPageDropdown'
import Pagination from 'features/Pagination'
import SortingArrow from 'components/SortingArrow'
import { SortingArrowWrapper } from 'components/SortingArrow/style'
import { delay } from 'utils/delay'
import { showErrorToast, showSuccessToast } from 'utils/toast'
import { usePagination } from 'hooks/usePagination'
import sortDirection from 'constants/sortDirection'
import roles from 'constants/roles'
import { CountriesContext } from 'providers/countries'
import ActionsMenu from 'features/ActionMenu'
import RemoveIcon from 'assets/icons/remove-icon16px'
import BlockIcon from 'assets/icons/block-icon'
import { EyeIcon, HideEyeIcon, CopyIcon, GenerateKeyIcon } from './icons'
import {
  requestGetBulkImportAccess,
  requestSearchBulkImportUsers,
  requestAddBulkImportAccess,
  requestRemoveBulkImportAccess,
  requestGenerateBulkImportUserApiKey,
  requestDeleteBulkImportUserApiKey
} from 'api/bulkImportAccess'
import {
  PageContainer,
  PageHeader,
  PageTitle,
  TableWrapper,
  ApiKeyCellWrap,
  MaskedKey,
  IconButton,
  FilterSearchWrapper,
  FilterWrapper,
  FilterItem,
  SearchWrapper,
  TableFooter
} from './style'

const defaultPaginationState = {
  totalNumber: 0,
  resultsPerPage: 12,
  totalPages: 0,
  currentPage: 1,
  hasNext: true,
  hasPrevious: false
}

const roleLabels = {
  1: 'Individual',
  2: 'NCG',
  3: 'RFP',
  4: 'Secretariat',
  5: 'BRC',
  6: 'BirdLife Secretariat'
}

const formatDate = (value) => {
  if (!value) return '-'
  return String(value).substring(0, 10).split('-').reverse().join('/')
}

const copyText = async (text) => {
  if (navigator.clipboard?.writeText && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text)
      return
    } catch (error) {
      // HTTP / permission — fall through to execCommand.
    }
  }

  const textarea = document.createElement('textarea')
  textarea.value = text
  textarea.setAttribute('readonly', '')
  textarea.style.position = 'fixed'
  textarea.style.left = '-9999px'
  document.body.appendChild(textarea)
  textarea.focus()
  textarea.select()
  try {
    if (!document.execCommand('copy')) throw new Error('copy failed')
  } finally {
    document.body.removeChild(textarea)
  }
}

const ApiKeyCell = ({ apiKey }) => {
  const intl = useIntl()
  const [isVisible, setIsVisible] = useState(false)

  if (!apiKey) return '-'

  const copyKey = async () => {
    try {
      await copyText(apiKey)
      showSuccessToast(<FormattedMessage id="bulkImportAccessApiKeyCopied" />, 'showBorder')
    } catch (error) {
      showErrorToast(<FormattedMessage id="somethingWentWrong" />, 'showBorder')
    }
  }

  return (
    <ApiKeyCellWrap>
      <MaskedKey>{isVisible ? apiKey : '••••••••••••••••'}</MaskedKey>
      <IconButton
        type="button"
        onClick={() => setIsVisible((current) => !current)}
        title={intl.formatMessage({
          id: isVisible ? 'bulkImportAccessHideApiKey' : 'bulkImportAccessShowApiKey'
        })}
        aria-label={intl.formatMessage({
          id: isVisible ? 'bulkImportAccessHideApiKey' : 'bulkImportAccessShowApiKey'
        })}
      >
        {isVisible ? <HideEyeIcon /> : <EyeIcon />}
      </IconButton>
      <IconButton
        type="button"
        onClick={copyKey}
        title={intl.formatMessage({ id: 'bulkImportAccessCopyApiKey' })}
        aria-label={intl.formatMessage({ id: 'bulkImportAccessCopyApiKey' })}
      >
        <CopyIcon />
      </IconButton>
    </ApiKeyCellWrap>
  )
}

const SortHeader = ({ id, labelId, sorting, onSort }) => (
  <SortingArrowWrapper>
    <FormattedMessage id={labelId} />
    <SortingArrow
      checked={sorting.sortDirection === sortDirection.ASC && sorting.sortBy === id}
      onChange={() => onSort(id)}
    />
  </SortingArrowWrapper>
)

let accessFetchAbortController = null
let userSearchAbortController = null
const searchUsersDelay = delay(300)

const BulkImportAccess = () => {
  const intl = useIntl()
  const { countriesOptions } = useContext(CountriesContext)
  const [rows, setRows] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [isAddOpen, setIsAddOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState(null)
  const [userOptions, setUserOptions] = useState([])
  const [userSearch, setUserSearch] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [countryCode, setCountryCode] = useState(null)
  const [userRole, setUserRole] = useState(null)
  const [apiKeyFilter, setApiKeyFilter] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [sorting, setSorting] = useState({
    sortBy: 'grantedAt',
    sortDirection: sortDirection.DESC
  })
  const { pagination, setPagination, selectPage, nextPage, prevPage, nextSection, prevSection, changeResultsPerPage } =
    usePagination(defaultPaginationState)

  const loadAccess = () => {
    if (accessFetchAbortController) {
      accessFetchAbortController.abort()
    }
    accessFetchAbortController = new AbortController()
    const isSearch = searchQuery.length >= 3

    setIsLoading(true)
    requestGetBulkImportAccess({
      resultsPerPage: pagination.resultsPerPage,
      currentPage: pagination.currentPage,
      query: isSearch ? searchQuery.trim() : '',
      role: !isSearch && userRole ? userRole.value : '',
      country: !isSearch && countryCode ? countryCode.value : '',
      hasApiKey: !isSearch && apiKeyFilter ? apiKeyFilter.value : '',
      sortBy: sorting.sortBy,
      sortDirection: sorting.sortDirection,
      signal: accessFetchAbortController.signal
    })
      .then((data) => {
        if (data?.error) {
          setRows([])
          showErrorToast(<FormattedMessage id="somethingWentWrong" />, 'showBorder')
          return
        }
        const nextRows = Array.isArray(data?.rows) ? data.rows : []
        const nextPagination = data?.pagination || defaultPaginationState
        if (nextPagination.totalPages > 0 && pagination.currentPage > nextPagination.totalPages) {
          setPagination((state) => ({ ...state, currentPage: nextPagination.totalPages }))
          return
        }
        setRows(nextRows)
        setPagination(nextPagination)
      })
      .catch((error) => {
        if (error?.name === 'AbortError') return
        showErrorToast(<FormattedMessage id="somethingWentWrong" />, 'showBorder')
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  useEffect(() => {
    if (searchQuery.length > 2) {
      setCountryCode(null)
      setUserRole(null)
      setApiKeyFilter(null)
    }
  }, [searchQuery])

  useEffect(() => {
    loadAccess()
  }, [
    countryCode,
    userRole,
    apiKeyFilter,
    pagination.resultsPerPage,
    pagination.currentPage,
    sorting.sortBy,
    sorting.sortDirection,
    searchQuery
  ])

  const handleFilterAction = (event, callback) => {
    setSearchQuery('')
    callback(event.value === null ? null : event)
    setPagination((state) => ({ ...state, currentPage: 1 }))
  }

  const clearFilters = () => {
    setCountryCode(null)
    setUserRole(null)
    setApiKeyFilter(null)
    setSearchQuery('')
  }

  const handleSearch = (event) => {
    const newQuery = event.target.value
    setSearchQuery(newQuery)
    if (newQuery.length > 2) {
      setPagination((state) => ({ ...state, currentPage: 1 }))
    }
  }

  const handleSort = (sortBy) => {
    if (sorting.sortBy === sortBy) {
      setSorting({
        sortBy,
        sortDirection: sorting.sortDirection === sortDirection.ASC ? sortDirection.DESC : sortDirection.ASC
      })
      return
    }
    setSorting({ sortBy, sortDirection: sortDirection.ASC })
  }

  const closeAdd = () => {
    setIsAddOpen(false)
    setSelectedUser(null)
    setUserSearch('')
    setUserOptions([])
  }

  const loadUserOptions = (query) => {
    if (userSearchAbortController) userSearchAbortController.abort()
    userSearchAbortController = new AbortController()
    requestSearchBulkImportUsers({ query, signal: userSearchAbortController.signal })
      .then((data) => {
        if (!Array.isArray(data)) return
        setUserOptions(
          data.map((user) => {
            const name = [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email
            return {
              value: user.id,
              label: user.role ? `${name} · ${user.email} · ${user.role}` : `${name} · ${user.email}`
            }
          })
        )
      })
      .catch((error) => {
        if (error?.name === 'AbortError') return
      })
  }

  useEffect(() => {
    if (!isAddOpen) return
    searchUsersDelay(() => loadUserOptions(userSearch.trim()))
  }, [isAddOpen, userSearch])

  const handleAdd = async () => {
    if (!selectedUser?.value) return
    setIsSaving(true)
    try {
      const result = await requestAddBulkImportAccess({ userId: selectedUser.value })
      if (result?.error || !result?.userId) {
        showErrorToast(result?.error || <FormattedMessage id="somethingWentWrong" />, 'showBorder')
        return
      }
      showSuccessToast(<FormattedMessage id="bulkImportAccessAdded" />, 'showBorder')
      closeAdd()
      if (pagination.currentPage === 1) loadAccess()
      else setPagination((state) => ({ ...state, currentPage: 1 }))
    } catch (error) {
      showErrorToast(<FormattedMessage id="somethingWentWrong" />, 'showBorder')
    } finally {
      setIsSaving(false)
    }
  }

  const handleRemove = async (userId) => {
    try {
      const result = await requestRemoveBulkImportAccess(userId)
      if (result?.error) {
        showErrorToast(result.error, 'showBorder')
        return
      }
      showSuccessToast(<FormattedMessage id="bulkImportAccessRemoved" />, 'showBorder')
      loadAccess()
    } catch (error) {
      showErrorToast(<FormattedMessage id="somethingWentWrong" />, 'showBorder')
    }
  }

  const handleGenerateApiKey = async (userId) => {
    try {
      const result = await requestGenerateBulkImportUserApiKey(userId)
      if (result?.error || !result?.apiKey) {
        showErrorToast(result?.error || <FormattedMessage id="somethingWentWrong" />, 'showBorder')
        return
      }
      showSuccessToast(<FormattedMessage id="bulkImportAccessApiKeyGenerated" />, 'showBorder')
      loadAccess()
    } catch (error) {
      showErrorToast(<FormattedMessage id="somethingWentWrong" />, 'showBorder')
    }
  }

  const handleDeleteApiKey = async (userId) => {
    try {
      const result = await requestDeleteBulkImportUserApiKey(userId)
      if (result?.error) {
        showErrorToast(result.error, 'showBorder')
        return
      }
      showSuccessToast(<FormattedMessage id="bulkImportAccessApiKeyDeleted" />, 'showBorder')
      loadAccess()
    } catch (error) {
      showErrorToast(<FormattedMessage id="somethingWentWrong" />, 'showBorder')
    }
  }

  const countryOptions = [
    { label: 'View All', value: null },
    ...countriesOptions.map((item) => ({ label: item.label, value: item.code }))
  ]
  const roleOptions = [
    { label: 'View All', value: null },
    ...roles.map((role) => ({ label: roleLabels[role.value] || role.label, value: role.value }))
  ]
  const apiKeyOptions = [
    { label: 'View All', value: null },
    { label: intl.formatMessage({ id: 'bulkImportAccessHasApiKey' }), value: 'true' },
    { label: intl.formatMessage({ id: 'bulkImportAccessNoApiKey' }), value: 'false' }
  ]

  const hasActiveFilters = Boolean(countryCode || userRole || apiKeyFilter || searchQuery.length > 2)

  const columns = [
    {
      id: 'name',
      header: <SortHeader id="name" labelId="name" sorting={sorting} onSort={handleSort} />,
      accessorKey: 'name',
      cell: ({ getValue }) => getValue()
    },
    {
      id: 'email',
      header: <SortHeader id="email" labelId="email" sorting={sorting} onSort={handleSort} />,
      accessorKey: 'email',
      cell: ({ getValue }) => getValue()
    },
    {
      id: 'role',
      header: <SortHeader id="role" labelId="role" sorting={sorting} onSort={handleSort} />,
      accessorKey: 'role',
      cell: ({ getValue }) => getValue()
    },
    {
      id: 'country',
      header: <SortHeader id="country" labelId="country" sorting={sorting} onSort={handleSort} />,
      accessorKey: 'country',
      cell: ({ getValue }) => getValue()
    },
    {
      id: 'apiKey',
      header: <FormattedMessage id="bulkImportAccessApiKey" />,
      accessorKey: 'apiKey',
      cell: ({ getValue }) => getValue()
    },
    {
      id: 'lastImportedAt',
      header: <SortHeader id="lastImportedAt" labelId="bulkImportAccessLastImport" sorting={sorting} onSort={handleSort} />,
      accessorKey: 'lastImportedAt',
      cell: ({ getValue }) => getValue()
    },
    {
      id: 'lastExportedAt',
      header: <SortHeader id="lastExportedAt" labelId="bulkImportAccessLastExport" sorting={sorting} onSort={handleSort} />,
      accessorKey: 'lastExportedAt',
      cell: ({ getValue }) => getValue()
    },
    {
      id: 'grantedAt',
      header: <SortHeader id="grantedAt" labelId="bulkImportAccessGrantedAt" sorting={sorting} onSort={handleSort} />,
      accessorKey: 'grantedAt',
      cell: ({ getValue }) => getValue()
    },
    {
      id: 'action',
      header: <FormattedMessage id="actions" />,
      accessorKey: 'action',
      cell: ({ getValue }) => getValue()
    }
  ]

  const tableRows = rows.map((row) => {
    const dropdownItems = row.apiKey
      ? [
          {
            icon: <RemoveIcon />,
            title: 'bulkImportAccessDeleteApiKey',
            onClick: () => handleDeleteApiKey(row.userId)
          }
        ]
      : [
          {
            icon: <GenerateKeyIcon />,
            title: 'generateApiKey',
            onClick: () => handleGenerateApiKey(row.userId)
          }
        ]

    dropdownItems.push({
      icon: <BlockIcon />,
      title: 'bulkImportAccessRemove',
      onClick: () => handleRemove(row.userId)
    })

    return {
      name: [row.firstName, row.lastName].filter(Boolean).join(' ') || '-',
      email: row.email,
      role: row.role || '-',
      country: row.country || '-',
      apiKey: <ApiKeyCell apiKey={row.apiKey} />,
      lastImportedAt: formatDate(row.lastImportedAt),
      lastExportedAt: formatDate(row.lastExportedAt),
      grantedAt: formatDate(row.grantedAt),
      action: <ActionsMenu dropdownItems={dropdownItems} />
    }
  })

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle data-test="legend">
          <FormattedMessage id="bulkImportAccess" />
        </PageTitle>
        <Button type="primary" height="40px" marginTop="0" onClick={() => setIsAddOpen(true)}>
          <FormattedMessage id="bulkImportAccessAddUser" />
        </Button>
      </PageHeader>
      <FilterSearchWrapper>
        <FilterWrapper data-test="quick-filter">
          <FilterItem>
            <Select
              field="country"
              value={countryCode}
              onChange={(event) => handleFilterAction(event, setCountryCode)}
              options={countryOptions}
              placeholder="country"
              height="40px"
              marginTop="0px"
            />
          </FilterItem>
          <FilterItem>
            <Select
              field="role"
              value={userRole}
              onChange={(event) => handleFilterAction(event, setUserRole)}
              options={roleOptions}
              placeholder="role"
              height="40px"
              marginTop="0px"
            />
          </FilterItem>
          <FilterItem>
            <Select
              field="hasApiKey"
              value={apiKeyFilter}
              onChange={(event) => handleFilterAction(event, setApiKeyFilter)}
              options={apiKeyOptions}
              placeholder="bulkImportAccessApiKey"
              height="40px"
              marginTop="0px"
            />
          </FilterItem>
        </FilterWrapper>
        <SearchWrapper>
          <SearchInput
            value={searchQuery}
            onChange={handleSearch}
            placeholder="bulkImportAccessSearch"
            type="text"
            dataTest="search-input"
          />
        </SearchWrapper>
      </FilterSearchWrapper>
      <TableWrapper>
        <Table
          rows={tableRows}
          columns={columns}
          noResultMessage={
            hasActiveFilters
              ? searchQuery
                ? 'nothingMatchesYourSearch'
                : 'noMatchingFilterCriteria'
              : 'bulkImportAccessEmpty'
          }
          dataTest="bulk-import-access-list"
          clearFilters={clearFilters}
          clearFiltersMessage={searchQuery ? 'clearResults' : 'clearFilters'}
          noClearFilter={!hasActiveFilters}
          isLoading={isLoading}
        />
      </TableWrapper>
      <TableFooter data-test="pagination">
        <ItemsPerPageDropdown
          currentPage={pagination.currentPage}
          resultsPerPage={pagination.resultsPerPage}
          totalNumber={pagination.totalNumber}
          onChange={changeResultsPerPage}
          dataTest="items-per-page"
        />
        {pagination.totalPages > 1 && (
          <Pagination
            activePages={pagination.currentPage}
            numberOfPages={pagination.totalPages}
            nextPage={nextPage}
            prevPage={prevPage}
            nextSection={nextSection}
            prevSection={prevSection}
            selectPage={selectPage}
            dataTest="page-number"
          />
        )}
      </TableFooter>
      <Modal
        isOpen={isAddOpen}
        toggleModal={closeAdd}
        modalTitleId="bulkImportAccessAddUser"
        width="560px"
      >
        <Select
          field="user"
          label="bulkImportAccessSearchUser"
          value={selectedUser}
          onChange={setSelectedUser}
          options={userOptions}
          placeholder="bulkImportAccessSearchUser"
          height="48px"
          marginTop="0px"
          marginBottom="0px"
          marginBottomLabel="8px"
          inputMargin="0 0 0 8px"
          isSearchable
          isClearable
          filterOption={() => true}
          onInputChange={(value, meta) => {
            if (meta?.action === 'input-change') setUserSearch(value)
          }}
          zIndex={1100}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px' }}>
          <Button height="40px" marginTop="0" onClick={closeAdd}>
            <FormattedMessage id="cancel" defaultMessage="Cancel" />
          </Button>
          <Button type="primary" height="40px" marginTop="0" onClick={handleAdd} isDisabled={isSaving || !selectedUser?.value}>
            <FormattedMessage id="bulkImportAccessAddUser" />
          </Button>
        </div>
      </Modal>
    </PageContainer>
  )
}

export default BulkImportAccess
